import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { devportalApi } from '../../../utils/devportalApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';

function maskToken(token) {
  if (!token) return '';
  const prefix = token.slice(0, 10);
  const suffix = token.slice(-6);
  return `${prefix}...${suffix}`;
}

const AccessTokensManager = () => {
  const { user } = useAuth();
  const { add: addToast } = useToast();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [justCreatedToken, setJustCreatedToken] = useState(null); // plaintext shown once

  const canLoad = useMemo(() => !!user?.id, [user?.id]);

  const loadTokens = async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const items = await devportalApi.listAccessTokens();
      // Enrich tokens with project information
      const enrichedTokens = (items || []).map(token => {
        const project = projects.find(p => p.id === token.project_id);
        return {
          ...token,
          project: project ? { id: project.id, name: project.name } : null
        };
      });
      setTokens(enrichedTokens);
    } catch (e) {
      console.error('Failed to load access tokens', e);
      setTokens([]);
      alert(e?.message || 'Failed to load access tokens');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTokens();
    const loadProjects = async () => {
      if (!canLoad) return;
      setProjectsLoading(true);
      try {
        const items = await devportalApi.listProjects();
        const normalized = (items || []).map(p => ({ id: p.id, name: p.name || p.title }));
        setProjects(normalized);
      } catch (e) {
        console.error('Failed to load projects', e);
        setProjects([]);
      }
      setProjectsLoading(false);
    };
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad]);

  // Reload tokens when projects are loaded to ensure project information is available
  useEffect(() => {
    if (projects.length > 0) {
      loadTokens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const handleCreateToken = async () => {
    if (!newTokenName.trim() || !user?.id) return;
    if (!selectedProjectId) {
      addToast({
        title: 'Project required',
        description: 'Please select a project for this token.',
        variant: 'destructive'
      });
      return;
    }
    setCreating(true);
    try {
      const data = await devportalApi.createAccessToken({
        name: newTokenName.trim(),
        project_id: selectedProjectId,
      });

      setJustCreatedToken({ 
        plaintext: data.token, 
        name: data.name, 
        created_at: data.created_at || new Date().toISOString() 
      });
      setNewTokenName('');
      setSelectedProjectId('');
      setShowCreateForm(false);
      await loadTokens();
      addToast({
        title: 'Access token created',
        description: 'Your access token has been created successfully. Copy it now; it will only be shown once.',
        variant: 'success'
      });
    } catch (e) {
      console.error('Failed to create token', e);
      addToast({
        title: 'Error',
        description: e?.message || 'Failed to create access token',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!id) return;
    setConfirmConfig({
      title: 'Revoke Access Token',
      message: 'Are you sure you want to revoke this token? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await devportalApi.revokeAccessToken(id);
          await loadTokens();
        } catch (e) {
          alert(e?.message || 'Failed to revoke token');
        }
      }
    });
    setConfirmOpen(true);
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
  };

  // Confirm modal state and component
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ title: '', message: '', onConfirm: null });

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Access Tokens</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => loadTokens()}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            Generate Token
          </Button>
        </div>
      </div>

      {showCreateForm && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h3 className="text-sm font-medium text-foreground mb-3">Create New Access Token</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <Input
                placeholder="Token name (e.g., CI/CD, Mobile App)"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e?.target?.value)}
                className="flex-1"
              />
            </div>
            <div className="flex gap-3">
              <Select
                className="flex-1"
                label="Project"
                placeholder={projectsLoading ? 'Loading projects...' : 'Select a project'}
                options={projects.map(p => ({ value: p.id, label: p.name }))}
                value={selectedProjectId}
                onChange={setSelectedProjectId}
                loading={projectsLoading}
                clearable
                searchable
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateToken}
                disabled={!newTokenName?.trim() || creating || !selectedProjectId}
              >
                {creating ? 'Creating...' : 'Create'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateForm(false)}
                disabled={creating}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {justCreatedToken && (
        <div className="mb-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-medium text-foreground mb-1">Copy your new access token now</h3>
              <p className="text-sm text-muted-foreground">This token will only be shown once. Store it securely. You can revoke it at any time.</p>
              <div className="mt-3 p-3 bg-background rounded border border-border font-mono break-all select-text">
                {justCreatedToken.plaintext}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Copy"
                iconPosition="left"
                onClick={() => copyToClipboard(justCreatedToken.plaintext)}
              >
                Copy
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setJustCreatedToken(null)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading tokens...</div>
        ) : tokens.length === 0 ? (
          <div className="text-sm text-muted-foreground">No access tokens yet.</div>
        ) : (
          tokens.map((t) => {
            const label = t.name || 'Untitled Token';
            const masked = `${t.token_prefix || ''}************${t.last_four || ''}`;
            return (
              <div key={t.id} className="p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <h3 className="font-medium text-foreground">{label}</h3>
                    {t.revoked_at ? (
                      <div className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">revoked</div>
                    ) : (
                      <div className="px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">active</div>
                    )}
                    {t.project && (
                      <div className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {t.project.name}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {!t.revoked_at && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleRevoke(t.id)}
                        title="Revoke"
                      >
                        <Icon name="Trash2" size={14} />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Token:</span>
                    <p className="font-mono text-foreground mt-1 break-all">{masked}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="text-foreground mt-1">{new Date(t.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <p className="text-foreground mt-1">{t.revoked_at ? `Revoked (${new Date(t.revoked_at).toLocaleString()})` : 'Active'}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Confirm Modal */}
      <Modal
        open={confirmOpen}
        title={confirmConfig.title || 'Please Confirm'}
        onClose={() => setConfirmOpen(false)}
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">{confirmConfig.message}</p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={async () => { setConfirmOpen(false); await confirmConfig.onConfirm?.(); }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AccessTokensManager;

// Simple modal component for confirmations
const Modal = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-2 w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>×</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};
